import type { MotorContract } from '@/types/motor/MotorContract'
import { getToken } from '@/utils/KeycloakUtils'
import { type AvailableKaskoProducts } from '@/types/motor/MotorInsurance'
import { type RequestHeaders } from '@/types/ServiceResponse'

function getCsrfMotorToken(): string | null {
  // Implementierung je nach Kontext
  return null
}

export async function createMotorHeaders(additionalHeaders = {}): Promise<HeadersInit> {
  const token = await getToken()
  const csrfToken = getCsrfMotorToken()

  const baseHeaders: RequestHeaders = {
    Authorization: `Bearer ${token}`,
    KeyId: globalThis.ENV_CONFIG?.API_KEY,
    'Content-Type': 'application/json',
    'X-Line': 'Motor'
  }
  if (csrfToken) {
    baseHeaders['X-CSRF-Token'] = csrfToken
  }

  return {
    ...baseHeaders,
    ...additionalHeaders
  }
}

export async function fetchMotorContractByPolicyId(policyId: string) {
  const headers = await createMotorHeaders()

  return fetch(`${globalThis.ENV_CONFIG?.SERVICE_HOSTS.MOTOR}/contracts/${policyId}`, {
    headers,
    method: 'GET'
  }).then(async (response) => {
    if (!response.ok) {
      throw new Error(response.statusText)
    } else if (response.status === 204) {
      return null
    }
    return await response.json()
  })
}

export async function fetchMotorQuoteByQuoteId(quoteId: string): Promise<MotorContract> {
  const headers = await createMotorHeaders()

  return fetch(
    `${globalThis.ENV_CONFIG?.SERVICE_HOSTS.MOTOR}/quotes?` +
      new URLSearchParams({
        quoteId: quoteId
      }),
    {
      headers,
      method: 'GET'
    }
  ).then(async (response) => {
    if (response.ok) {
      return await response.json()
    } else {
      throw new Error('Fehler beim Laden der Motor-Daten')
    }
  })
}

export async function fetchMotorVehicleChangeDataByPolicyId(
  policyId: string
): Promise<{ data: string; code: number }> {
  const headers = await createMotorHeaders()

  return fetch(`${globalThis.ENV_CONFIG?.SERVICE_HOSTS.MOTOR}/change/type/${policyId}`, {
    headers,
    method: 'GET'
  }).then(async (response) => {
    return { data: await response.text(), code: response.status }
  })
}

export async function requestMotorDocuments(params: {
  email: string | null
  policyId: string
  partnerProxyId: string | null
}): Promise<boolean> {
  const headers = await createMotorHeaders()

  try {
    const response = await fetch(
      `${globalThis.ENV_CONFIG?.SERVICE_HOSTS.MOTOR}/motor/conclusions/contract-documents-sendings`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(params)
      }
    )

    return response.ok
  } catch {
    return false
  }
}

export async function logTerminationReason(
  partnerProxyId: string,
  policyId: string,
  reason: { value: string; description: string }
): Promise<void> {
  const headers = await createMotorHeaders()

  const url = `${globalThis.ENV_CONFIG?.SERVICE_HOSTS.MOTOR}/log/terminationreason/${partnerProxyId}/${policyId}`

  try {
    await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(reason)
    })
  } catch (_error) {
    console.error('Error during fetch:', _error)
  }
}

export async function fetchComprehensiveProducts(
  tarif: string
): Promise<AvailableKaskoProducts> {
  const headers = await createMotorHeaders()

  const response = await fetch(
    `${globalThis.ENV_CONFIG?.SERVICE_HOSTS.MOTOR}/comprehensiveProducts/${tarif}`,
    {
      method: 'GET',
      headers
    }
  )

  if (!response.ok) {
    throw new Error('Failed to fetch comprehensive products')
  }

  return await response.json()
}

export async function fetchMotorContractsForSelfService(
  partnerProxyId: string
): Promise<any[]> {
  const headers = await createMotorHeaders()

  const response = await fetch(
    `${globalThis.ENV_CONFIG?.SERVICE_HOSTS.MOTOR}/contracts/selfService/${partnerProxyId}`,
    {
      method: 'GET',
      headers
    }
  )

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Unauthorized')
    }
    throw new Error(`Error fetching motor contracts: ${response.statusText}`)
  }

  return await response.json()
}

export async function requestAccompaniedDrivingCertificate(
  policyId: string,
  firstname: string,
  lastname: string,
  dateOfBirth: string
): Promise<Response> {
  const token = await getToken()
  const parts = dateOfBirth.split('.').map((p) => p.trim())
  // parts[0] = "31" (Tag)
  // parts[1] = "12" (Monat, Dezember = 11)
  // parts[2] = "2009" (Jahr)

  // ISO-String (YYYY-MM-DD) direkt aus den Teilen bilden, ohne über
  // new Date(...).toISOString() zu gehen. Dieser Umweg würde das lokale
  // Datum in UTC umrechnen und dabei je nach Zeitzone einen Tag verschieben.
  const day = parts[0]!.padStart(2, '0')
  const month = parts[1]!.padStart(2, '0')
  const year = parts[2]!
  const isoDateOfBirth = `${year}-${month}-${day}`

  return fetch(
    `${globalThis.ENV_CONFIG?.SERVICE_HOSTS.MOTOR}/certificates/accompanied-driving`,
    {
      method: 'POST',
      body: JSON.stringify({
        policyId,
        firstnameOfTheTeenager: firstname,
        lastnameOfTheTeenager: lastname,
        dateOfBirthOfTheTeenager: isoDateOfBirth
      }),
      headers: {
        Authorization: `Bearer ${token}`,
        KeyId: globalThis.ENV_CONFIG?.API_KEY,
        'Content-Type': 'application/json'
      }
    }
  )
}
